/** @typedef {'blox' | 'particles' | 'retro' | 'cinematic'} SplashVariantId */

export const SPLASH_STORAGE_KEY = 'zflix_splash_variant';
export const SPLASH_SOUND_KEY = 'zflix_splash_sound';
/** Legacy session key (cleared on sign-out); intro no longer skips on refresh. */
export const SPLASH_SHOWN_SESSION_KEY = 'zflix_splash_shown';
export const SPLASH_MIN_MS = 1200;

/** Blox bounce wall-hit times (seconds from splash anim start). */
export const SPLASH_BOING_IMPACT_TIMES_S = [1.0, 1.75, 2.55];

/** Three impacts (~2.55s), wordmark fade, 2.5s lockup hold. */
export const SPLASH_BLOX_BOUNCE_MS = 2550;
export const SPLASH_BLOX_WORDMARK_FADE_MS = 800;
export const SPLASH_BLOX_LOCKUP_HOLD_MS = 2500;
/** Fade in tagline after this delay from lockup start (last ~1s of hold). */
export const SPLASH_BLOX_LOCKUP_TAGLINE_DELAY_MS = 1500;
export const SPLASH_BLOX_LOCKUP_TAGLINE_FADE_MS = 500;
export const SPLASH_BLOX_MIN_MS = SPLASH_BLOX_BOUNCE_MS + SPLASH_BLOX_LOCKUP_HOLD_MS;

let bloxTimingCache = null;

/** @returns {{ bounceMs: number; lockupHoldMs: number; lockupTaglineDelayMs: number; lockupTaglineFadeMs: number; minMs: number }} */
export function getSplashBloxTiming() {
  if (bloxTimingCache) return bloxTimingCache;
  bloxTimingCache = {
    bounceMs: SPLASH_BLOX_BOUNCE_MS,
    wordmarkFadeMs: SPLASH_BLOX_WORDMARK_FADE_MS,
    lockupHoldMs: SPLASH_BLOX_LOCKUP_HOLD_MS,
    lockupTaglineDelayMs: SPLASH_BLOX_LOCKUP_TAGLINE_DELAY_MS,
    lockupTaglineFadeMs: SPLASH_BLOX_LOCKUP_TAGLINE_FADE_MS,
    minMs: SPLASH_BLOX_MIN_MS,
  };
  return bloxTimingCache;
}

/** Production default intro (Blox bounce). Used when storage is unset or legacy `bounce`. */
/** @type {SplashVariantId} */
export const DEFAULT_SPLASH_VARIANT = 'blox';

/** @type {{ id: SplashVariantId; label: string; blurb: string }[]} */
export const SPLASH_VARIANT_OPTIONS = [
  { id: 'blox', label: 'Blox bounce', blurb: 'Z drops in, bounces with boings and a red glow, then wordmark fades in' },
  { id: 'particles', label: 'Particle burst', blurb: 'Color sparks swirl into the Z' },
  { id: 'retro', label: 'Retro scan', blurb: '8-bit scanlines and arcade glow' },
  { id: 'cinematic', label: 'Cinematic', blurb: 'Soft zoom with a warm tagline' },
];

/** @param {string | null | undefined} raw */
export function parseSplashVariant(raw) {
  if (raw === 'bounce') return DEFAULT_SPLASH_VARIANT;
  const ids = SPLASH_VARIANT_OPTIONS.map((o) => o.id);
  if (raw && ids.includes(/** @type {SplashVariantId} */ (raw))) return /** @type {SplashVariantId} */ (raw);
  return DEFAULT_SPLASH_VARIANT;
}

export function readSplashVariant() {
  if (typeof window === 'undefined') return DEFAULT_SPLASH_VARIANT;
  try {
    const raw = window.localStorage.getItem(SPLASH_STORAGE_KEY);
    if (raw === 'bounce') {
      writeSplashVariant(DEFAULT_SPLASH_VARIANT);
      return DEFAULT_SPLASH_VARIANT;
    }
    return parseSplashVariant(raw);
  } catch {
    return DEFAULT_SPLASH_VARIANT;
  }
}

/** @param {SplashVariantId} id */
export function writeSplashVariant(id) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SPLASH_STORAGE_KEY, id);
  } catch (_) {}
}

export function readSplashSoundEnabled() {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(SPLASH_SOUND_KEY) !== '0';
  } catch {
    return true;
  }
}

/** @param {boolean} on */
export function writeSplashSoundEnabled(on) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SPLASH_SOUND_KEY, on ? '1' : '0');
  } catch (_) {}
}

export function isSplashPreviewQuery() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('splashPreview') === '1';
  } catch {
    return false;
  }
}

export function readSplashShownThisSession() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(SPLASH_SHOWN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** @deprecated Intro runs on every load; kept for API compatibility. */
export function writeSplashShownThisSession() {}

export function clearSplashShownThisSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SPLASH_SHOWN_SESSION_KEY);
  } catch (_) {}
}

/** @returns {'pending' | 'done'} */
export function initialSplashPhase() {
  if (typeof window === 'undefined') return 'pending';
  if (isSplashPreviewQuery()) return 'done';
  return 'pending';
}
